import { api } from './client';

/**
 * Counter staff sign in with a username and a password, unlike every other app
 * in the estate, which sends a one-time code to a phone.
 *
 * The till is why. It is a shared PC on a shop floor: the person standing at it
 * does not have their own phone in hand, the shop's number is not theirs to
 * receive a code on, and waiting for an SMS with a customer at the counter is
 * the wrong shape for the job.
 *
 * The trade is worth naming rather than hiding: a password is something known
 * where an OTP is something held, so this is the weaker of the two on its own.
 * What buys it back is how little the account reaches — counter staff cannot
 * open an online order, cannot void a bill, and see only their own takings. The
 * backend refuses the OTP path for this role outright, so there is one door and
 * not two.
 */

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

export interface AuthUser {
  id: string;
  fullName: string;
  username: string | null;
  role: string;
}

export interface LoginResult extends AuthTokens {
  user: AuthUser;
}

/**
 * What the signed-in role may do, as switched by the Super Admin on the admin
 * panel's Roles & Permissions screen. The API refuses whatever is not listed;
 * the till reads this so it does not offer a tab or a button that would only
 * be refused.
 */
export interface MyPermissions {
  role: string;
  superAdmin: boolean;
  /** False once this role has been taken off the till altogether. */
  appAccess?: boolean;
  permissions: string[];
}

export const authApi = {
  login: (username: string, password: string) =>
    api.postPublic<LoginResult>('/auth/counter/login', { username, password }),
  myPermissions: () => api.get<MyPermissions>('/permissions/me'),
};

/**
 * Only these roles have any business on a billing terminal. A customer account
 * that somehow signs in here would see endpoints it cannot call, so we stop it
 * at the door with a message that explains itself.
 */
export const ALLOWED_ROLES = ['COUNTER_STAFF', 'ADMIN_OP', 'ADMIN', 'ADMIN_SUB'];

export function canUsePos(role: string | undefined): boolean {
  return !!role && ALLOWED_ROLES.includes(role);
}

/**
 * What the shop calls each role, as against what the database calls it.
 *
 * The enum itself is `COUNTER_STAFF` — spelled out that way because `UserRole`
 * also holds `WAREHOUSE_STAFF`, and a permission check reading a bare `STAFF`
 * next to it says nothing about which staff it means. On the top bar there is
 * no such ambiguity: the only staff at a till are the counter's, so the pill
 * reads simply "Staff".
 */
const ROLE_LABELS: Record<string, string> = {
  COUNTER_STAFF: 'Staff',
  ADMIN_OP: 'Super Admin',
  ADMIN: 'Admin',
  ADMIN_SUB: 'Sub Admin',
};

export function roleLabel(role: string | undefined): string {
  if (!role) return '';
  return ROLE_LABELS[role] ?? role;
}
